import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import Marquee from 'react-fast-marquee';
import {
  FileText, BarChart3, Building2, Users, Shield, Clock, ArrowRight,
  CheckCircle2, ClipboardList, Search, FileDown, X, Star,
  Truck, ChevronRight,
} from 'lucide-react';

const DASHBOARD_IMG = 'https://static.prod-images.emergentagent.com/jobs/b78bca1c-ce27-4fe8-b78e-c2a98138abf0/images/eb59ebfa8feb22d518f7eeb6e1b2d0b12e7cff418b74a725e7a305e4a8d8d6bb.png';

const features = [
  { icon: ClipboardList, title: 'Multi-Step Intake', desc: 'Guided claim submission with loss type, document checklist, and file uploads.', color: 'text-teal-600 bg-teal-50' },
  { icon: BarChart3, title: 'Live Dashboard', desc: 'Real-time statistics, pie charts by company, status, and fault.', color: 'text-indigo-500 bg-indigo-50' },
  { icon: Building2, title: 'Company Management', desc: 'Manage fleet divisions or terminals. Dedicated company dashboards.', color: 'text-amber-600 bg-amber-50' },
  { icon: Users, title: 'Team & Roles', desc: 'Admin, Manager, Member, Viewer roles with invite links.', color: 'text-rose-500 bg-rose-50' },
  { icon: Shield, title: 'Audit & Recovery', desc: 'Full audit trail. Recover deleted claims and documents instantly.', color: 'text-emerald-600 bg-emerald-50' },
  { icon: FileDown, title: 'Export & Reports', desc: 'CSV/PDF exports by company, date, loss type, or status.', color: 'text-sky-600 bg-sky-50' },
];

const steps = [
  { num: '01', title: 'Submit a Claim', desc: 'Capture all details — driver, loss type, documents — in a guided intake form.', icon: FileText, color: 'bg-teal-600' },
  { num: '02', title: 'Track & Manage', desc: 'Monitor through New, In Process, and Closed. Add notes, upload docs.', icon: Search, color: 'bg-indigo-500' },
  { num: '03', title: 'Analyze & Report', desc: 'See trends on your dashboard. Filter by company, date, or type. Export anytime.', icon: BarChart3, color: 'bg-amber-500' },
];

const testimonials = [
  {
    quote: 'Finally, a claims system that understands trucking. The intake form alone saves us hours every week.',
    name: 'Mike R.',
    role: 'Fleet Safety Manager',
    img: 'https://images.unsplash.com/photo-1695395860103-38d172403a46?w=120&h=120&fit=crop&crop=face',
  },
  {
    quote: 'We switched from spreadsheets and never looked back. The dashboard gives us instant visibility.',
    name: 'Sarah T.',
    role: 'Operations Director',
    img: 'https://images.unsplash.com/photo-1762522921456-cdfe882d36c3?w=120&h=120&fit=crop&crop=face',
  },
  {
    quote: 'The role-based access is perfect. Our adjusters see what they need, nothing more.',
    name: 'James L.',
    role: 'Claims Supervisor',
    img: 'https://images.pexels.com/photos/7540490/pexels-photo-7540490.jpeg?auto=compress&w=120&h=120&fit=crop',
  },
];

const logoCompanies = [
  'Crawford Trucking', 'Brenny Specialized', 'Chief Carriers', 'Decker Truck Line',
  'Leonard\'s Express', 'TransLand', 'Peninsula Truck Lines',
  'Continental Express', 'S&H Express',
];

const comparisonLeft = [
  'Lost files across email threads',
  'Manual data entry errors',
  'No audit trail or accountability',
  'Hours building status reports',
  'No visibility across companies',
];
const comparisonRight = [
  'Every document in one place',
  'Guided intake eliminates errors',
  'Full audit log of every action',
  'Real-time dashboard analytics',
  'Cross-org provider visibility',
];

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function trackEvent(name) {
  if (window.posthog) window.posthog.capture(name);
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white font-body" data-testid="landing-page">
      {/* ─── Navbar ─── */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/logo.png" alt="CMS" className="w-8 h-8" />
            <span className="text-lg font-heading font-bold text-slate-700 tracking-tight">CMS</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/about" className="text-sm font-medium text-gray-500 hover:text-slate-700 transition-colors hidden sm:inline" data-testid="landing-about-link">About</Link>
            <Link to="/pricing" className="text-sm font-medium text-gray-500 hover:text-slate-700 transition-colors hidden sm:inline" data-testid="landing-pricing-link">Pricing</Link>
            <Link to="/login" className="text-sm font-medium text-gray-500 hover:text-slate-700 transition-colors" data-testid="landing-login-link">Sign In</Link>
            <Link to="/login" onClick={() => trackEvent('landing_hero_cta_click')}>
              <button className="hidden sm:inline-flex items-center gap-2 bg-teal-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-teal-700 transition-colors shadow-sm" data-testid="nav-cta-btn">
                Start Free Trial
              </button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-teal-50/30" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <FadeUp>
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-teal-600 bg-teal-50 px-3 py-1.5 rounded-full mb-6">
                  <Truck className="w-3.5 h-3.5" />
                  Built for Trucking & Logistics
                </span>
              </FadeUp>
              <FadeUp delay={0.1}>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight text-slate-700" data-testid="hero-title">
                  Claims management,{' '}
                  <span className="bg-gradient-to-r from-teal-600 to-indigo-500 bg-clip-text text-transparent">simplified.</span>
                </h1>
              </FadeUp>
              <FadeUp delay={0.2}>
                <p className="text-base lg:text-lg text-gray-500 leading-relaxed mt-5 max-w-md">
                  From first notice of loss to final resolution — manage every claim, document, and report in one place.
                </p>
              </FadeUp>
              <FadeUp delay={0.3}>
                <div className="flex flex-col sm:flex-row gap-3 mt-8">
                  <Link to="/login" onClick={() => trackEvent('landing_hero_trial_click')}>
                    <button className="flex items-center justify-center gap-2 bg-teal-600 text-white text-sm font-semibold px-7 py-3.5 rounded-lg hover:bg-teal-700 transition-colors shadow-sm w-full sm:w-auto" data-testid="hero-cta-trial">
                      Start Free Trial <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                  <Link to="/login?demo=solo" onClick={() => trackEvent('landing_hero_demo_click')}>
                    <button className="flex items-center justify-center gap-2 border border-gray-300 text-slate-600 text-sm font-semibold px-7 py-3.5 rounded-lg hover:border-slate-400 hover:text-slate-700 transition-colors w-full sm:w-auto" data-testid="hero-cta-demo">
                      Try the Demo <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
                <p className="text-xs text-gray-400 mt-4">30-day free trial. No credit card required.</p>
              </FadeUp>
            </div>

            <FadeUp delay={0.3} className="relative">
              <div className="relative rounded-xl border border-gray-200 shadow-xl shadow-gray-200/50 overflow-hidden bg-white" data-testid="hero-dashboard-mockup">
                <img src={DASHBOARD_IMG} alt="CMS Dashboard Preview" className="w-full h-auto" loading="eager" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white border border-gray-200 shadow-lg rounded-xl px-4 py-3 hidden lg:flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-50 flex items-center justify-center rounded-lg">
                  <BarChart3 className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium">Claims resolved</p>
                  <p className="text-lg font-heading font-bold text-slate-700">+247 this month</p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Logos Strip ─── */}
      <section className="border-y border-gray-100 bg-gray-50/50 py-6" data-testid="logos-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 text-center mb-5">Trusted by fleet companies nationwide</p>
          <Marquee speed={30} gradient gradientColor="#fafafa" gradientWidth={80}>
            {logoCompanies.map((name) => (
              <div key={name} className="mx-12 flex items-center gap-2 opacity-40 hover:opacity-80 transition-opacity">
                <Truck className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-heading font-semibold text-gray-500 whitespace-nowrap">{name}</span>
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-indigo-500">Features</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-700 mt-3">Everything you need</h2>
              <p className="text-base text-gray-500 mt-3 max-w-lg mx-auto">A complete toolkit for your claims workflow — from intake to analytics.</p>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.07}>
                <div
                  className="p-6 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all bg-white group"
                  data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 ${f.color}`}>
                    <f.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-heading font-bold text-slate-700 mb-1.5">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Comparison ─── */}
      <section className="py-20 lg:py-28 bg-gray-50" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Why Switch</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-700 mt-3">Spreadsheets vs. CMS</h2>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            <FadeUp>
              <div className="bg-red-50 p-8 rounded-xl border border-red-100">
                <h3 className="font-heading text-lg font-bold text-red-800 mb-5 flex items-center gap-2">
                  <X className="w-5 h-5 text-red-400" />
                  The Old Way
                </h3>
                <ul className="space-y-3.5">
                  {comparisonLeft.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-red-700/80">
                      <X className="w-4 h-4 text-red-300 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="bg-white p-8 rounded-xl border border-teal-100">
                <h3 className="font-heading text-lg font-bold text-teal-800 mb-5 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-teal-500" />
                  The CMS Way
                </h3>
                <ul className="space-y-3.5">
                  {comparisonRight.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-indigo-500">How It Works</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-700 mt-3">Three steps. Total clarity.</h2>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {steps.map((step, i) => (
              <FadeUp key={step.num} delay={i * 0.1}>
                <div className="p-7 rounded-xl border border-gray-100 bg-white relative overflow-hidden" data-testid={`step-${step.num}`}>
                  <span className="font-heading text-6xl font-extrabold text-gray-100 absolute -top-1 -right-1 select-none leading-none">{step.num}</span>
                  <div className="relative">
                    <div className={`w-12 h-12 ${step.color} rounded-lg flex items-center justify-center mb-5`}>
                      <step.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-slate-700 mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Testimonials ─── */}
      <section className="py-20 lg:py-28 bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Testimonials</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-700 mt-3">Trusted by claims professionals</h2>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <FadeUp key={i} delay={i * 0.1}>
                <div className="bg-white rounded-xl border border-gray-100 p-7 hover:shadow-md transition-shadow h-full flex flex-col" data-testid={`testimonial-${i}`}>
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-base font-medium text-slate-600 leading-relaxed mb-6 flex-grow">"{t.quote}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100 mt-auto">
                    <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                    <div>
                      <p className="text-sm font-bold text-slate-700">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Pricing Preview ─── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="pricing-preview-section">
        <div className="max-w-7xl mx-auto px-6">
          <FadeUp>
            <div className="text-center mb-14">
              <span className="text-xs font-semibold uppercase tracking-widest text-indigo-500">Pricing</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-700 mt-3">Plans that grow with you</h2>
              <p className="text-base text-gray-500 mt-3 max-w-lg mx-auto">Start as a single company. Scale to a multi-org provider.</p>
            </div>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {[
              { name: 'Solo', price: '$39', audience: 'Trucking Companies', accent: 'border-teal-200 hover:border-teal-400' },
              { name: 'Provider', price: '$99', audience: 'Claims Service Providers', accent: 'border-indigo-200 hover:border-indigo-400' },
              { name: 'Enterprise', price: '$349', audience: 'Insurance Providers', soon: true, accent: 'border-gray-200 hover:border-gray-300' },
            ].map((plan, i) => (
              <FadeUp key={plan.name} delay={i * 0.1}>
                <div
                  className={`p-7 rounded-xl border-2 ${plan.accent} transition-all bg-white hover:shadow-md h-full`}
                  data-testid={`preview-${plan.name.toLowerCase()}`}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">{plan.soon ? 'Coming Soon' : plan.name}</span>
                  <p className="text-sm font-medium mt-1.5 text-gray-500">{plan.audience}</p>
                  <p className="text-4xl font-heading font-extrabold mt-3 text-slate-700">
                    {plan.price}<span className="text-sm font-normal text-gray-400">/mo</span>
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.3}>
            <div className="text-center mt-10">
              <Link to="/pricing" onClick={() => trackEvent('landing_view_pricing_click')}>
                <button className="inline-flex items-center gap-2 border border-gray-300 text-slate-600 text-sm font-semibold px-7 py-3 rounded-lg hover:border-slate-400 hover:text-slate-700 transition-colors" data-testid="view-pricing-btn">
                  View Full Pricing <ChevronRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="relative py-24 lg:py-28 overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
        <div
          className="absolute inset-0 opacity-10 bg-cover bg-center"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1766785368863-f2188a8c8b32?w=1600&q=60")' }}
        />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <FadeUp>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-white leading-tight">
              Ready to take control of your claims?
            </h2>
            <p className="text-base text-slate-400 mt-4 max-w-lg mx-auto">
              Join trucking companies that streamlined their claims process. Start your free trial today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Link to="/register" onClick={() => trackEvent('landing_final_cta_trial_click')}>
                <button className="flex items-center gap-2 bg-teal-600 text-white text-sm font-semibold px-7 py-3.5 rounded-lg hover:bg-teal-500 transition-colors shadow-sm" data-testid="final-cta-trial">
                  Start Free Trial <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/login?demo=solo" onClick={() => trackEvent('landing_final_cta_demo_click')}>
                <button className="flex items-center gap-2 border border-white/20 text-white text-sm font-semibold px-7 py-3.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="final-cta-demo">
                  Try the Demo <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-slate-900 py-16 lg:py-20" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-16">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <img src="/logo.png" alt="CMS" className="w-8 h-8 brightness-200" />
                <span className="text-lg font-heading font-bold text-white tracking-tight">CMS</span>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">Claims management platform built for the trucking industry.</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-4">Product</p>
              <ul className="space-y-3">
                <li><Link to="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/login?demo=solo" className="text-sm text-slate-400 hover:text-white transition-colors">Demo</Link></li>
                <li><Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors">Sign Up</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-4">Features</p>
              <ul className="space-y-3">
                <li><span className="text-sm text-slate-400">Intake Forms</span></li>
                <li><span className="text-sm text-slate-400">Dashboard</span></li>
                <li><span className="text-sm text-slate-400">Document Mgmt</span></li>
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-4">Company</p>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-sm text-slate-400 hover:text-white transition-colors">About Us</Link></li>
                <li><a href="https://allaboutclaims.us/contact-us" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</a></li>
                <li><Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-14 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-600">CMS By All About Claims. All rights reserved.</p>
            <p className="text-xs text-slate-700">Built for trucking. Powered by data.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
